import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createUseStyles } from "react-jss";

import FixedSectionScroller from "@/base/components/FixedSectionScroller/FixedSectionScroller";
import { Retry } from "@/base/components/Global/Retry";
import { useAppDispatch, useAppSelector } from "@/store";
import { retryProductLayout } from "@/store/products/actions";
import {
  selectIsProductLayoutFailed,
  selectIsProductLayoutLoading,
  selectSections,
} from "@/store/products/selectors";
import type { SectionListItem } from "@/store/products/types";

import ProductListSkeleton from "./ProductListSkeleton";
import SectionsList from "./SectionsList";

// TODO: move these constants somewhere more appropriate
export const SECTIONS_SELECTOR_HEIGHT = 76;
export const SECTION_TITLE_HEIGHT = 88;
export const CATEGORIES_SELECTOR_HEIGHT = 56;
export const ITEM_MARGIN_BOTTOM = 12;

const useStyles = createUseStyles(({ spacing }) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 54,
  },
  topContainer: {
    marginTop: spacing.x3l,
    position: "relative",
  },
}));

const ProductList: React.FC<{ showShadows?: boolean }> = ({
  showShadows = false,
}) => {
  const classes = useStyles();
  const { t } = useTranslation("global");
  const dispatch = useAppDispatch();
  const sectionList = useRef<HTMLDivElement>(null);
  const [activeSection, setActiveSection] = useState<SectionListItem | null>(
    null,
  );
  const sections = useAppSelector(selectSections);
  const isLoading = useAppSelector(selectIsProductLayoutLoading);
  const isFailed = useAppSelector(selectIsProductLayoutFailed);

  const handleRetry = useCallback(() => {
    void dispatch(retryProductLayout());
  }, [dispatch]);

  useEffect(() => {
    if (activeSection === null && sections && sections.length > 0) {
      setActiveSection(sections[0]);
    }
  }, [sections]);

  if (!sections) {
    if (isLoading) {
      return <ProductListSkeleton />;
    }
    if (isFailed) {
      return (
        <Retry
          hintText={t("RetryLoader.hint")}
          buttonText={t("RetryLoader.buttonText")}
          onRetry={handleRetry}
        />
      );
    }
    return null;
  }

  return (
    <div ref={sectionList} className={classes.topContainer}>
      <FixedSectionScroller
        sectionListRef={sectionList}
        activeSection={activeSection}
        sections={sections}
      />
      <div className={classes.container}>
        <SectionsList
          sections={sections || []}
          setActiveSection={setActiveSection}
          activeSection={activeSection}
          showShadows={showShadows}
        />
      </div>
    </div>
  );
};

export default ProductList;
